import { Directive, effect, inject, input, TemplateRef, ViewContainerRef } from '@angular/core';
import { SessionManagerService } from '../storage/session-manager.service';
import { hasPermission } from './permission.func';
import { PERMISSION_RESOURCE } from './permissions.types';

/**
 * Use details: This is a structural directive that conditionally includes a template based on user permissions.
 * So you need to use it like this: *lvrPermission="'Permission.Name'"* or *lvrPermission="['Permission.One', 'Permission.Two']"*
 * It doesn't work as an attribute directive.
 * @example
 * <div *lvrPermission="'User.Create'">
 *   This content is only visible to users with the 'User.Create' permission.
 * </div>
 */
@Directive({
	selector: '[lvrPermission]',
})
export class LvrPermissionDirective {
	private readonly session = inject(SessionManagerService);
	private readonly templateRef = inject(TemplateRef<unknown>);
	private readonly viewContainer = inject(ViewContainerRef);

	public readonly lvrPermission = input.required<PERMISSION_RESOURCE | PERMISSION_RESOURCE[]>();

	private hasView = false;

	public constructor() {
		effect(() => {
			const allowed = hasPermission(this.session.permissions, this.lvrPermission())();

			if (allowed && !this.hasView) {
				this.viewContainer.createEmbeddedView(this.templateRef);
				this.hasView = true;
			} else if (!allowed && this.hasView) {
				this.viewContainer.clear();
				this.hasView = false;
			}
		});
	}
}
